import {Component, OnInit, ViewChild} from '@angular/core';
import {DataObjEditComponent, DataObjEditComponentHelper, Select2Data} from '../../rest-service';
import {CallClass, TariffClass, TariffClassService} from '../../tariff-class.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {GlobalValidator} from '../../global-validator';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {NotificationService} from '../../notification.service';
import {Observable} from 'rxjs/Observable';
import {SelectComponent} from 'ng2-select';
import {UserService} from '../../user.service';

@Component({
  selector: 'app-tariff-class-edit',
  templateUrl: './tariff-class-edit.component.html',
  styleUrls: ['./tariff-class-edit.component.css']
})
export class TariffClassEditComponent implements OnInit, DataObjEditComponent<TariffClass> {

  // name of the object in the singular
  readonly vowelSingular = 'a Tarifa';

  readonly entityPath = '/tariff_class';

  callClassesSelectData: Select2Data[] = Object.keys(CallClass)
    .filter((value, index, array) => value.length > 1 && value !== 'UNKNOWN')
    .map((value, index, array) => {
      return {id: value, text: value}
    });

  @ViewChild('select_call_type') selectCallClasses: SelectComponent;

  helper: DataObjEditComponentHelper<TariffClass>;

  readonly defaultObj = new TariffClass(null, null, null, [], 30, 6,
    0.05, 0.01, Date.now() / 1000, 3, 0, false);

  controlsConfigs = [{
    'description': ['', Validators.required],
    'callClasses': [''],
    'initialTime': [30, [GlobalValidator.intValueValidator, GlobalValidator.minValueValidator(0), Validators.required]],
    'incrementTime': [6, [GlobalValidator.intValueValidator, GlobalValidator.minValueValidator(0), Validators.required]],
    'initialCost': [0.05, [Validators.required, GlobalValidator.minValueValidator(0)]],
    'incrementCost': [0.01, [Validators.required, GlobalValidator.minValueValidator(0)]],
    'minimumDuration': [3, [GlobalValidator.intValueValidator, GlobalValidator.minValueValidator(0)]],
    'ignoreDuration': [0, [GlobalValidator.intValueValidator, GlobalValidator.minValueValidator(0)]],
    'validSince': ['', Validators.required],
  }];

  validationMessages = [
    {
      'description': {
        'required': 'Descrição não informada'
      },
      'callClasses': {
        'required': 'Classes de Chamadas não informadas'
      },
      'initialTime': {
        'required': 'Tempo inicial não informado',
        'minValue': 'O tempo inicial precisa ser maior que zero',
      },
      'incrementTime': {
        'required': 'Tempo de incremento não informado',
        'minValue': 'O tempo de incremento precisa ser maior que zero',
      },
      'initialCost': {
        'required': 'Custo inicial não informado',
        'minValue': 'O custo inicial precisa ser maior que zero',
      },
      'incrementCost': {
        'required': 'Custo de incremento não informado',
        'minValue': 'O custo de incremento precisa ser maior que zero',
      },
      'minimumDuration': {
        'required': 'Duração mínima não informada',
        'minValue': 'A Duração mínima precisa ser maior que zero',
      },
      'ignoreDuration': {
        'required': 'Duração a ser ignorada não informada',
        'minValue': 'A Duração a ser ignorada precisa ser maior que zero',
      },
    }
  ];

  constructor(private fb: FormBuilder, public router: Router, public route: ActivatedRoute, public ns: NotificationService,
              public service: TariffClassService, public us: UserService) {
    this.helper = new DataObjEditComponentHelper(fb, this.controlsConfigs, this.validationMessages, this.entityPath,
      this.router, this.ns, this.service, this);
  }

  ngOnInit() {
    this.helper.buildForm();
    this.selectCallClasses.active = [];
    this.route.params.switchMap((params: Params) => {
      const id = params['id'];
      return this.processId(id);
    }).subscribe((obj: TariffClass) => {
      this.helper.actualObj = obj;
      this.refreshModel(obj);
    });
    this.helper.formGroups.forEach((formGroup, index, array) =>
      formGroup.valueChanges.debounceTime(250).subscribe((data) => this.helper.onValueChanges(data, index)));
  }

  processId(id: string) {
    if (id && id !== '') {
      this.helper.editing = true;
      return this.service.getById(id);
    } else {
      this.helper.editing = false;
      return Observable.of(this.defaultObj);
    }
  }

  validateFields(index: number, defaultFormErrors: any): Observable<any> {
    if (index === 0) {
      if (this.selectCallClasses.active.length < 1) {
        if (!this.helper.formGroups[0].get('callClasses').pristine) {
          defaultFormErrors['callClasses'] = ['Classes de Chamadas não informadas'];
        }
        this.helper.formInvalid[index] = true;
      }
    }
    return Observable.of(defaultFormErrors);
  }

  trySave(index: number, model: any) {
    this.helper.preSave();
    console.log(`Trying to save: ${JSON.stringify(model)}`);
    this.helper.isAllValid(index).subscribe((valid) => {
      if (valid) {
        console.log(`Trying to send to the Rest Service...`);
        const obj = this.fromModel(model);
        this.helper.decideOperation(obj).subscribe((res) => {
          this.helper.processResult(index, res);
          this.helper.formGroups.forEach((formGroup, idx, array) =>
            formGroup.valueChanges.debounceTime(250).subscribe((data) => this.helper.onValueChanges(data, idx)));
          if (this.helper.editing) {
            this.refreshModel(obj);
          } else {
            this.refreshModel(this.defaultObj);
          }
        }, (error) => {
          console.log(`Error Saving object: '${obj}': ${JSON.stringify(error)}`);
          this.ns.error(error.toString(), 'Erro ao salvar');
        }, () => this.helper.postSave());
      } else {
        console.log(`NotSaving because something is invalid...`);
        this.helper.postSave();
      }
    }, (error) => {
      this.ns.error(error.toString(), 'Erro ao salvar');
    });
  }

  refreshModel(obj: TariffClass) {
    this.selectCallClasses.active = [];
    this.toModel(obj, this.helper.formGroups[0]);
  }

  fromModel(model: any): TariffClass {
    let id = null;
    if (this.helper.editing) {
      id = this.helper.actualObj.id;
    }
    const callClasses = this.selectCallClasses.active.map((value, index, array) => value.id);
    return new TariffClass(id, model.description, this.us.currentUser.getValue().customerDatabaseId, callClasses,
      model.initialTime, model.incrementTime, model.initialCost, model.incrementCost, model.validSince / 1000, model.minimumDuration,
      model.ignoreDuration, false);
  }

  toModel(obj: TariffClass, fg: FormGroup) {
    fg.controls['description'].patchValue(obj.description);
    fg.controls['initialTime'].patchValue(obj.initialTime);
    fg.controls['incrementTime'].patchValue(obj.incrementTime);
    fg.controls['initialCost'].patchValue(obj.initialCost);
    fg.controls['incrementCost'].patchValue(obj.incrementCost);
    fg.controls['minimumDuration'].patchValue(obj.minimumDuration);
    fg.controls['ignoreDuration'].patchValue(obj.ignoreDuration);
    fg.controls['validSince'].patchValue(new Date(obj.validSince * 1000));
    let callClasses = obj.callClasses.map((value, index, array) => {
      return {id: value, text: value};
    })
    this.selectCallClasses.active = callClasses;
    // fg.controls['range'].patchValue('');
  }

  setCallClasses(data: any) {
    console.log(`${JSON.stringify(data)}`);
    this.helper.formGroups[0].controls['callClasses'].patchValue(data);
  }
}



// WEBPACK FOOTER //
// /Users/tabelini/development/jvm/tenacity/pysix/PysixCloud/src/app/tariff-class/tariff-class-edit/tariff-class-edit.component.ts